import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Sparkline } from "@outpave/ds";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Inline } from "components/layout/reusableLayouts";
import CustomSkeleton from "components/common/CustomSkeleton";

export interface TrendPoint {
  date: string;
  value: number;
}

interface TrendChartProps {
  /** Oldest point first. */
  series: TrendPoint[];
  /** Formats a point's value for the tooltip. */
  formatValue: (value: number) => string;
  loading?: boolean;
}

/** Dates under the curve, the first and last pinned to its ends. */
const AXIS_LABELS = 5;

const labelIndices = (points: number): number[] => {
  const count = Math.min(AXIS_LABELS, points);
  if (count <= 1) return [0];
  return Array.from({ length: count }, (_, i) =>
    Math.round((i * (points - 1)) / (count - 1)),
  );
};

const formatDay = (date: string) => dayjs(date).format("MMM D");

/** Headroom above and below the series, as a share of its own spread. */
const DOMAIN_PADDING = 0.25;

/**
 * The window the curve is drawn in. An area series anchors its axis to zero
 * unless the domain says otherwise, and a balance moves by hundreds on a base
 * of hundreds of thousands: against a zero baseline the line flattens and the
 * fill becomes a solid block. So the domain is the series' own range plus
 * headroom, and a genuinely flat series gets an arbitrary band around itself
 * so the line lands mid-panel instead of on an edge.
 */
const domainFor = (values: number[]): { min: number; max: number } => {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const spread = hi - lo;
  const padding =
    spread > 0 ? spread * DOMAIN_PADDING : Math.abs(hi) * 0.01 || 1;

  return { min: lo - padding, max: hi + padding };
};

/**
 * A value over time, as the prototype draws it: the package Sparkline's glow
 * fill and live end dot, over a row of dates whose first and last sit flush
 * with the curve's ends. Axis ticks would centre those two on the plot's
 * edges, where the package clips them.
 */
const TrendChart: React.FC<TrendChartProps> = ({
  series,
  formatValue,
  loading = false,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ flex: 1, minHeight: 160 }}>
        <CustomSkeleton variant="graph" />
      </Box>
    );
  }

  if (series.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 160,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("No Data Found")}
        </Typography>
      </Box>
    );
  }

  const values = series.map((point) => point.value);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
      }}
    >
      <Box sx={{ position: "relative", flex: 1, minHeight: 200 }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Sparkline
            data={values}
            xAxis={{ data: series.map((point) => formatDay(point.date)) }}
            area
            fill
            // Zero side margins put the first and last points over the first
            // and last dates below.
            margin={{ top: 18, right: 0, bottom: 18, left: 0 }}
            yAxis={domainFor(values)}
            valueFormatter={formatValue}
            sx={{ height: "100%" }}
          />
        </Box>
      </Box>
      <Inline justify="between" wrap={false}>
        {labelIndices(series.length).map((index) => (
          <Typography
            key={series[index].date}
            variant="caption"
            sx={{ color: "var(--color-text-tertiary)" }}
          >
            {formatDay(series[index].date)}
          </Typography>
        ))}
      </Inline>
    </Box>
  );
};

export default TrendChart;
